/**
 * usePanelDrag — the drag half of a panel surface, in ONE place.
 *
 * Dragging a panel is not the panel's own business: `Panel` reports pointer
 * events from its header, but the drop ZONES live on the surface, and the
 * pointer leaves the header the instant a drag begins. Something above both
 * has to own the gesture.
 *
 * THIS EXISTS BECAUSE IT DRIFTED. The research surface had the window-level
 * net; the review surface, ported from the same layout model, did not — so a
 * drag there could be started and never finished: the pointer left the header,
 * no further events arrived, the drop zones stayed on screen over the resize
 * edges, and the page looked frozen. Tables had no drag wiring at all. Three
 * surfaces, three different answers to the same question. Now there is one.
 *
 * Two rules the net enforces:
 *
 *   • A DRAG NEVER SURVIVES THE GESTURE THAT ENDED IT. Release anywhere —
 *     including outside the window — finishes it; pointercancel and Escape
 *     abandon it. The listeners are attached only while a drag is live.
 *   • ZONES ARE HIT-TESTED, NOT NEGOTIATED. They are `pointer-events-none`,
 *     so this reads the DOM directly rather than relying on drop targets.
 *     HTML5 drag-and-drop was the original approach and never completed a
 *     drop between absolutely-positioned docks.
 */

import { useCallback, useEffect, useState } from 'react';
import { DockSide } from './panelLayout';

export interface PanelDropTarget {
  dock: DockSide;
  index: number;
}

export interface UsePanelDragResult<Id extends string> {
  /** The panel being dragged, or null. */
  dragging: Id | null;
  /** `"left:1"` — the zone under the pointer, for highlighting. */
  hoveredZone: string | null;
  /** True when zones should be on screen at all. */
  active: boolean;
  /** Wire these three onto every draggable `Panel`. */
  onDragStart: (id: Id) => void;
  onDragMove: (x: number, y: number) => void;
  onDragEnd: (x?: number, y?: number) => void;
  /** Is this exact zone under the pointer? */
  isHovered: (dock: DockSide, index: number) => boolean;
}

/**
 * Which drop zone is under this point?
 *
 * Exported for tests: the geometry is the part jsdom cannot exercise, so the
 * decision is at least inspectable on its own.
 */
export function zoneAtPoint(x: number, y: number): PanelDropTarget | null {
  if (typeof window === 'undefined') return null;
  const zones = Array.from(
    window.document.querySelectorAll<HTMLElement>('[data-dropzone="true"]')
  );
  const hit = zones.find((zone) => {
    const r = zone.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  });
  if (!hit) return null;
  return {
    dock: hit.dataset.dock as DockSide,
    index: Number(hit.dataset.index || 0),
  };
}

export function usePanelDrag<Id extends string>(
  /** Where the panel should land. Not called when released off every zone. */
  onDrop: (id: Id, target: PanelDropTarget) => void,
  /** Drag is unavailable on a narrow viewport, where nothing floats. */
  enabled = true
): UsePanelDragResult<Id> {
  const [dragging, setDragging] = useState<Id | null>(null);
  const [hoveredZone, setHoveredZone] = useState<string | null>(null);

  const onDragStart = useCallback((id: Id) => {
    if (enabled) setDragging(id);
  }, [enabled]);

  const onDragMove = useCallback((x: number, y: number) => {
    const zone = zoneAtPoint(x, y);
    setHoveredZone(zone ? `${zone.dock}:${zone.index}` : null);
  }, []);

  const onDragEnd = useCallback((x?: number, y?: number) => {
    const id = dragging;
    setDragging(null);
    setHoveredZone(null);
    if (!id || typeof x !== 'number' || typeof y !== 'number') return;
    const zone = zoneAtPoint(x, y);
    // Released outside every zone: the panel stays where it was, rather than
    // jumping somewhere the user never aimed at.
    if (zone) onDrop(id, zone);
  }, [dragging, onDrop]);

  useEffect(() => {
    if (!dragging) return;
    const move = (e: PointerEvent) => onDragMove(e.clientX, e.clientY);
    const up = (e: PointerEvent) => onDragEnd(e.clientX, e.clientY);
    const cancel = () => { setDragging(null); setHoveredZone(null); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') cancel(); };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('keydown', key);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('keydown', key);
    };
  }, [dragging, onDragMove, onDragEnd]);

  return {
    dragging,
    hoveredZone,
    active: !!dragging && enabled,
    onDragStart,
    onDragMove,
    onDragEnd,
    isHovered: useCallback(
      (dock: DockSide, index: number) => hoveredZone === `${dock}:${index}`,
      [hoveredZone]
    ),
  };
}

export default usePanelDrag;
